import {
  FooterMenuColumn,
  FooterSocial,
  HeaderMenuItem,
} from "@sunday/component";
import { HeaderMenu, Language } from "../Header/IHeader";
import { ICTH_FOOTER_ASSET_BASE_URL } from "./constants";

type Translate = (key: string) => string;

type NavigationOptions = {
  language: Language;
  translate: Translate;
  environmentStage?: string;
};

type HeaderNavigationOptions = NavigationOptions & {
  customMenus?: HeaderMenu;
};

type SocialConfig = {
  instagramLink?: string;
  linkedinLink?: string;
  facebookLink?: string;
  youtubeLink?: string;
};

const getExternalUrl = (language: Language): string =>
  `https://easysunday.com/${language}`;

export const buildRetailUrl = (
  language: Language,
  environmentStage = process.env.NEXT_PUBLIC_ENVIRONMENT_STAGE
): string => {
  if (!environmentStage || environmentStage === "production") {
    return `https://icecream.co.th/${language}`;
  }
  if (environmentStage === "development") {
    return `http://localhost:3000/${language}`;
  }
  return `https://${environmentStage}.icecream.co.th/${language}`;
};

export const buildICTHHeaderMenus = ({
  language,
  translate,
  environmentStage,
  customMenus,
}: HeaderNavigationOptions): HeaderMenuItem[] => {
  const externalUrl = getExternalUrl(language);
  const retailUrl = buildRetailUrl(language, environmentStage);

  return [
    {
      label: translate("header.buyOnline"),
      href: retailUrl,
      showSubMenu: false,
      subMenus: [
        {
          label: translate("header.healthInsurance.title"),
          href: retailUrl,
          items: [
            {
              label: translate("header.healthInsurance.lumpSum"),
              href: `${retailUrl}/health`,
            },
            {
              label: translate("header.healthInsurance.coPay"),
              href: `${retailUrl}/health-co-pay`,
            },
            {
              label: translate("header.healthInsurance.child"),
              href: `${retailUrl}/axa-child-health`,
            },
            {
              label: translate("header.healthInsurance.allProducts"),
              href: retailUrl,
            },
          ],
        },
        {
          label: translate("header.motorInsurance.title"),
          href: `${externalUrl}/motor`,
          items: [
            {
              label: translate("header.motorInsurance.ev"),
              href: `${retailUrl}/sunday-car-insurance-ev`,
            },
            {
              label: translate("header.motorInsurance.motor"),
              href: `${retailUrl}/sunday-car-insurance`,
            },
            {
              label: translate("header.motorInsurance.allProducts"),
              href: `${externalUrl}/motor`,
            },
            {
              label: translate("header.motorInsurance.claim"),
              href: `${externalUrl}/motor/claim`,
            },
            {
              label: translate("header.motorInsurance.sundayPartner"),
              href: `https://sundayinsurance.co.th/${language}/services/panel/?type=garage`,
            },
          ],
        },
        {
          label: translate("header.onlinePersonalAccidentInsurance"),
          href: `${retailUrl}/#accident`,
          items: [
            {
              label: translate("header.healthInsurance.personalAccident"),
              href: `${retailUrl}/#accident`,
            },
          ],
        },
        {
          label: translate("header.onlineTravelInsurance"),
          href: `${retailUrl}/#travel`,
          items: [
            {
              label: translate("header.travelInsurance"),
              href: `${retailUrl}/#travel`,
            },
          ],
        },
        {
          label: translate("header.onlineLifeInsurance"),
          href: `${retailUrl}/#life`,
          items: [
            {
              label: translate("header.lifeInsurance"),
              href: `${retailUrl}/#life`,
            },
          ],
        },
        {
          label: translate("header.sundayHealthForBusiness.title"),
          href: `${externalUrl}/our-product/health`,
          items: [
            {
              label: translate("header.sundayHealthForBusiness.about"),
              href: `${externalUrl}/our-product/health`,
            },
            {
              label: translate(
                "header.sundayHealthForBusiness.forBusinessOwner"
              ),
              href: `${externalUrl}/our-product/health#owner`,
            },
            {
              label: translate("header.sundayHealthForBusiness.forEmployee"),
              href: `${externalUrl}/our-product/health#employee`,
            },
            {
              label: translate(
                "header.sundayHealthForBusiness.sundayHospitalNetwork"
              ),
              href: `https://sundayinsurance.co.th/${language}/services/panel?type=hospital-suncare`,
            },
          ],
        },
      ],
    },
    {
      label:
        customMenus?.sundayAffiliate?.label ||
        translate("header.sundayAffiliate"),
      href:
        customMenus?.sundayAffiliate?.href ||
        "https://icecream.co.th/affiliate",
    },
    {
      label: customMenus?.faq?.label || translate("header.faq"),
      href: customMenus?.faq?.href || `${externalUrl}/faq`,
    },
    {
      label: customMenus?.blog?.label || translate("header.blog"),
      href: customMenus?.blog?.href || "https://easysunday.com/blog",
    },
    {
      label: customMenus?.contactUs?.label || translate("header.contactus"),
      href: customMenus?.contactUs?.href || `${retailUrl}/contact-us`,
    },
    {
      label: customMenus?.aboutUs?.label || translate("header.about"),
      href: customMenus?.aboutUs?.href || "https://sundayinsholdings.com",
    },
  ];
};

export const buildICTHFooterMenus = ({
  language,
  translate,
  environmentStage,
}: NavigationOptions): FooterMenuColumn[] => {
  const externalUrl = getExternalUrl(language);
  const retailUrl = buildRetailUrl(language, environmentStage);

  return [
    {
      sections: [
        {
          title: translate("header.buyOnline"),
          items: [
            {
              label: translate("footer.products.motor"),
              href: `${retailUrl}/sunday-car-insurance`,
            },
            {
              label: translate("footer.products.ev"),
              href: `${retailUrl}/sunday-car-insurance-ev`,
            },
            {
              label: translate("footer.products.health"),
              href: `${retailUrl}/health`,
            },
            {
              label: translate("footer.products.childHealth"),
              href: `${retailUrl}/axa-child-health`,
            },
            {
              label: translate("header.healthInsurance.personalAccident"),
              href: `${retailUrl}/#accident`,
            },
            {
              label: translate("header.lifeInsurance"),
              href: `${retailUrl}/#life`,
            },
            {
              label: translate("header.travelInsurance"),
              href: `${retailUrl}/#travel`,
            },
          ],
        },
      ],
    },
    {
      sections: [
        {
          title: translate("footer.help"),
          items: [
            {
              label: translate("header.faq"),
              href: `${externalUrl}/faq`,
            },
            {
              label: translate("footer.findGarage"),
              href: `https://sundayinsurance.co.th/${language}/services/panel/?type=garage`,
            },
            {
              label: translate("footer.insuranceTerms"),
              href: "https://easysunday.com/blog/12-keywords-that-people-who-buy-car-insurance-must-know/",
            },
          ],
        },
        {
          title: translate("header.about"),
          items: [
            {
              label: translate("footer.ourStory"),
              href: "https://sundayinsholdings.com",
            },
          ],
        },
      ],
    },
    {
      sections: [
        {
          title: translate("header.others"),
          items: [
            {
              label: translate("header.blog"),
              href: "https://easysunday.com/blog",
            },
            {
              label: translate("header.contactus"),
              href: `${retailUrl}/contact-us`,
            },
            {
              label: translate("header.privacy"),
              href: `/${language}/privacy`,
            },
            {
              label: translate("header.terms"),
              href: `/${language}/terms-and-conditions`,
            },
            {
              label: translate("header.refundPolicy"),
              href: `/${language}/refund-policy`,
            },
          ],
        },
      ],
    },
  ];
};

export const buildICTHFooterSocials = (
  socialConfig?: SocialConfig
): FooterSocial[] => {
  const socialEntries: Array<{
    imageName: string;
    imageFileName: string;
    href?: string;
  }> = [
    {
      imageName: "Instagram",
      imageFileName: "instagram-white.svg",
      href: socialConfig?.instagramLink,
    },
    {
      imageName: "LinkedIn",
      imageFileName: "linkedin-white.svg",
      href: socialConfig?.linkedinLink,
    },
    {
      imageName: "Facebook",
      imageFileName: "facebook-white.svg",
      href: socialConfig?.facebookLink,
    },
    {
      imageName: "YouTube",
      imageFileName: "youtube-white.svg",
      href: socialConfig?.youtubeLink,
    },
  ];

  return socialEntries
    .filter(({ href }) => !!href)
    .map(({ imageName, imageFileName, href }) => ({
      imageSrc: `${ICTH_FOOTER_ASSET_BASE_URL}/${imageFileName}`,
      imageName,
      href: href as string,
    }));
};
